import _ from 'lodash'
import { Button, message } from 'antd'
import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { uploadToGetStaticUrl } from '../../api/file'
import ReactQuill, { Quill } from 'react-quill'
import { base64toBlob, toBase64 } from '../../utils/common'
import QuillImageDropAndPaste from 'quill-image-drop-and-paste'
import { updateMessages, sendMessage, getMessages } from '../../api/message'
import { saveRocketChatDraft } from '../../api/rocketChat'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'
import { deltaToMarkdown } from 'quill-delta-to-markdown'
import 'react-quill/dist/quill.snow.css'
import { isDraft } from 'immer'
import { loadUser } from '../../utils/storage'

const DraftIndicator = styled.div`
  font-size: 12px;
  color: #60656d;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  
  span {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #264382;
    border-radius: 50%;
    margin-right: 8px;
  }
`

const SavingIndicator = styled.div`
  font-size: 12px;
  color: #60656d;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  
  span {
    display: inline-block;
    width: 8px;
    height: 8px;
    background-color: #1890ff;
    border-radius: 50%;
    margin-right: 8px;
    animation: pulse 1s infinite;
  }
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`

Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste)

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image'
]

export default function Editor(props) {
  const { value, id, text, close, isChatPage, users, draftMessage, isRocketChat, clientId, personId } = props
  const [readOnly, setRadOnly] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [filteredUsers, setFilteredUsers] = useState([])
  const [showUserSuggestions, setShowUserSuggestions] = useState(false)
  const [isDraftMode, setIsDraftMode] = useState(!!draftMessage)
  const [isSaving, setIsSaving] = useState(false)
  const [isInitialDraftLoad, setIsInitialDraftLoad] = useState(!!draftMessage)
let editorRef = useRef(null)
let debounceTimerRef = useRef(null)
let autosaveTimeoutRef = useRef(null)
let initialDraftContentRef = useRef(null)

  const modules = {
    toolbar: {
      handlers: {
        image: imageHandler
      },
      container: [
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [
          { list: 'ordered' },
          { list: 'bullet' },
          { indent: '-1' },
          { indent: '+1' }
        ],
        ['link', 'image']
      ]
    },
    // imageDrop: {
    //   handler: imageDropHandler
    // },
    imageDropAndPaste: {
      handler: imageDropHandler
    }
  }

  let preRange = { index: 0, length: 0 }

  useEffect(() => {
    const { quillEditor } = getEditor()
    if (quillEditor) {
      let initialValue = value
      
      // If we have a draft message, use its content
      if (draftMessage && draftMessage.rich_text) {
        initialValue = draftMessage.rich_text
      } 
      // Otherwise, use existing logic for replies/editing
      else if (!value && text) {
        if (typeof text === 'string') {
          initialValue = `<p>${text}</p>`
        } else if (text.type === 'image') {
          initialValue = `<img src=${text.url} alt="image"/>`
        }
      }
      
      if (initialValue) {
        // Store initial draft content before loading it
        if (draftMessage && draftMessage.rich_text) {
          initialDraftContentRef.current = draftMessage.rich_text
        }
        
        quillEditor.clipboard.dangerouslyPasteHTML(initialValue)
        quillEditor.setSelection({
          index: quillEditor.getContents().length(),
          length: 0
        })
        
      } else {
        console.log('No initial value to load')
      }
    }
  }, [editorRef.current, draftMessage, value])

  // Update isDraftMode when draftMessage prop changes
  useEffect(() => {
    const { quillEditor } = getEditor()
    if (draftMessage) {
      setIsDraftMode(true)
      if (draftMessage.rich_text && !initialDraftContentRef.current) {
        initialDraftContentRef.current = draftMessage.rich_text
        if (quillEditor) {
          quillEditor.clipboard.dangerouslyPasteHTML(draftMessage.rich_text)
          quillEditor.setSelection({
            index: quillEditor.getContents().length(),
            length: 0
          })
        }
      }
    } else {
      if (!id) {
        setIsDraftMode(false)
        setIsInitialDraftLoad(false)
        initialDraftContentRef.current = null
        if (quillEditor) {
          quillEditor.setContents([])
        }
      }
    }
  }, [draftMessage])

  const [savedRange, setSavedRange] = useState(null)

  const handleEditorInput = e => {    
    const getEditor = _.get(editorRef, 'current.getEditor')
    const quillEditor = getEditor && getEditor()
    
    // Send message with 2-second delay (debounced)
    if (quillEditor) {
      const text = quillEditor.getText()
      const delta = quillEditor.getContents()
      const converter = new QuillDeltaToHtmlConverter(delta.ops, {})
      const richText = converter.convert()
      
      // Don't send draft if content is empty or just contains line breaks
      if (!text.trim() || richText === '<p><br/></p>') {
        return
      }
      
      // Skip draft call on initial load with draft message
      if (isInitialDraftLoad) {
        setIsInitialDraftLoad(false)
        return
      }
      
      // Skip draft save when editing existing messages (has id prop)
      if (id) {
        return
      }
      
      // Skip draft save if content hasn't changed from initial draft
      if (initialDraftContentRef.current && richText === initialDraftContentRef.current) {
        return
      }
            
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      
      // Set new timer
      debounceTimerRef.current = setTimeout(async () => {
        try {
          setIsSaving(true)
          
          if (isRocketChat) {
            // Use Rocket Chat draft API
            await saveRocketChatDraft({
              client_id: clientId,
              sender: personId,
              room_id: props.roomId, // Need to pass room_id for Rocket Chat
              rich_text: richText,
              rich_text_ops: delta.ops
            })
          } else {
            // Use old message draft API
            await sendMessage({ rich_text: richText, rich_text_ops: delta.ops }, props.clientId, true)
          }
          
          setIsSaving(false)
        } catch (error) {
          console.error('Draft save failed:', error)
          setIsSaving(false)
        }
      }, 2000)
    }

    if (!quillEditor) return

    const editorText = e.slice() // copy string
    const lastWord = editorText.replace('<p>', '').split(' ').pop()

    if (lastWord.startsWith('@')) {
      const selection = quillEditor.getSelection()
      if (selection) setSavedRange(selection)
      const query = lastWord.replace('</p>', '').slice(1)
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(query.toLowerCase())
      )
      setFilteredUsers(filtered)
      setShowUserSuggestions(true)
    } else {
      setShowUserSuggestions(false)
    }
  }

  const handleUserSelect = user => {
    const getEditor = _.get(editorRef, 'current.getEditor')
    const quillEditor = getEditor && getEditor()
    if (!quillEditor || !savedRange) return

    // go back to where @ was typed
    quillEditor.setSelection(savedRange.index, savedRange.length)

    // delete the @partial text
    const textBefore = quillEditor.getText(0, savedRange.index)
    const lastAt = textBefore.lastIndexOf('@')
    if (lastAt !== -1) {
      quillEditor.deleteText(lastAt, savedRange.index - lastAt)
      quillEditor.insertText(lastAt, ` @${user.name} `)
      quillEditor.setSelection(lastAt + user.name.length + 3, 0)
    }

    setShowUserSuggestions(false)
  }

  return (
    <>
      {isSaving && (
        <SavingIndicator>
          <span></span>
          Saving...
        </SavingIndicator>
      )}
      {isDraftMode && !isSaving && (
        <DraftIndicator>
          <span></span>
          Draft
        </DraftIndicator>
      )}
      <ReactQuill
        ref={editorRef}
        theme="snow"
        modules={modules}
        formats={formats}
        placeholder="Leave a message"
        onKeyDown={onKeyDown}
        readOnly={readOnly}
        defaultValue={value}
        onChange={handleEditorInput}
      />
      <div className="submit-button">
        <div />
        <div className="btns">
          {id && (
            <Button size="large" disabled={submitting} onClick={close}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            onClick={submit}
            disabled={submitting || props.loading}
            loading={submitting}
          >
            Submit
          </Button>
        </div>
      </div>
      {showUserSuggestions && (
        <div
          style={{
            position: 'absolute',
            bottom: '120%',
            left: 0,
            backgroundColor: 'white',
            border: filteredUsers.length > 0 ? '1px solid #ccc': '',
            borderRadius: '4px',
            zIndex: 1000,
            width: '200px'
          }}
        >
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              style={{
                padding: '8px',
                cursor: 'pointer'
              }}
              onClick={() => handleUserSelect(user)}
            >
              {user.name}
            </div>
          ))}
        </div>
      )}
    </>
  )

  function getEditor() {
    const getEditor = _.get(editorRef, 'current.getEditor')
    const quillEditor = getEditor && getEditor()
    let delta, range
    if (quillEditor) {
      delta = quillEditor.getContents()
      range = quillEditor.getSelection()
    }
    return { quillEditor, delta, range }
  }

  async function submit() {
    console.log('Submitting message')
    const { clientId, id, onSubmitCallback } = props
    const { quillEditor, delta } = getEditor()
    // await test()
    const _delta = delta //await handleSubmitImages(_.cloneDeep(delta))
    const converter = new QuillDeltaToHtmlConverter(delta.ops, {})
    const richText = converter.convert()
    setSubmitting(true)
    setRadOnly(true)

    // Clear draft mode immediately when user clicks Submit
    setIsDraftMode(false)

    if (isChatPage) {
      const text = quillEditor.getText()
      onSubmitCallback(richText, text)
      
      // Clear any pending draft timers immediately
      if (debounceTimerRef.current) {
        console.log('Clearing pending draft timer before content clearing')
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      
      // Clear content
      quillEditor.setContents([])
      
      setSubmitting(false)
      setRadOnly(false)
      return
    }

    const text = quillEditor.getText()
    if (!text.trim() && !_.includes(richText, '<img')) {
      return
    }
    try {
      if (id) {
        // update message
        await updateMessages(
          { rich_text: richText, rich_text_ops: _delta.ops },
          clientId,
          id
        )
        onSubmitCallback(richText)
        close()
      } else {
        quillEditor.setContents([{ insert: '\n' }])
        const { messages } = await sendMessage(
          { rich_text: richText, rich_text_ops: _delta.ops },
          clientId, false
        )
        onSubmitCallback(messages.reverse())
      }
      
      setSubmitting(false)
      setRadOnly(false)
    } catch (err) {
      message.error(err.message)
      setSubmitting(false)
      setRadOnly(false)
    }
  }

  function onKeyDown(event) {
    const { range } = getEditor()
    preRange = range
  }

  async function imageDropHandler(dataUrl, type, imageData) {
    const { quillEditor } = getEditor()
    if (quillEditor) {
      try {
        let [contentType, realData] = dataUrl.split(';')
        contentType = contentType.replace('data:', '')
        realData = realData.split(',')[1]
        const file = base64toBlob(realData, contentType)
        let url
        if (file.type === 'image/svg+xml') {
          url = dataUrl
        } else {
          url = await uploadImage(props.clientId, file)
        }

        quillEditor.insertEmbed(preRange.index, 'image', url)
        quillEditor.setSelection(preRange)
      } catch (err) {
        console.error(err)
        message.error('Upload image failed, please try again.')
      }
    }
  }

  function imageHandler() {
    const { quillEditor, range: editorRange } = getEditor()
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()
    input.onchange = async () => {
      try {
        const file = input.files[0]
        let url
        if (file.type === 'image/svg+xml') {
          url = await toBase64(file)
        } else {
          url = await uploadImage(props.clientId, file)
        }
        const range = editorRange || preRange
        quillEditor.insertEmbed(range.index, 'image', url)
        quillEditor.setSelection(range)
      } catch (err) {
        message.error('Upload image failed, please try again.')
      }
    }
  }

  async function uploadImage(clientId, file) {
    const { url } = await uploadToGetStaticUrl({
      personId: clientId,
      file
    })
    return url
  }
}
